/**
 * Which bones on THIS rig are hands, and what does the graft need to know about
 * each one — the first question the Hand Studio door has to answer, before a
 * human can be asked anything.
 *
 * Sourced from the MAPPING, never from bone names. `L_Hand` is Tripo's spelling,
 * `J_Bip_L_Hand` is VRoid's, `mixamorig:LeftHand` is Mixamo's, and the whole
 * point of the ontology is that a consumer never learns any of them. A name
 * regex here would be a fourth bone-map copy in a repo that already measured
 * what four copies cost ([[vrm-target-family-and-v2-machinery]]).
 *
 * The forearm is the graft's CUT — `isolateHandRegion` uses it to decide where
 * the hand stops being a hand. It is nullable in the Document because a rig may
 * genuinely lack one, and the isolator falls back to a wrist-local cut; a rig
 * that has one and is not told is a worse cut, not a failure.
 */
import type { Ontology, RigDescriptor } from '@harryhausen/kernel';

export interface HandCandidate {
  /** The rig's own name for the hand bone — what the Document declares. */
  readonly handBone: string;
  /** The rig's forearm bone, or null when the rig has none mapped. */
  readonly forearmBone: string | null;
  readonly side: 'left' | 'right';
  /** The ontology id this hand mapped to — evidence, not decoration. */
  readonly ontologyId: string;
  /**
   * Bones already parented to the hand. NON-EMPTY means the rig has fingers (or
   * something) below the wrist, and grafting onto it is a caller error the
   * kernel refuses (`graftDigits` guards the ontology-id collision). The door
   * shows this rather than hiding the hand: "already has fingers" is an answer.
   */
  readonly existingChildren: readonly string[];
}

const HANDS = [
  { hand: 'LeftHand', forearm: 'LeftForeArm', side: 'left' },
  { hand: 'RightHand', forearm: 'RightForeArm', side: 'right' },
] as const;

/**
 * Every hand this rig maps, with the evidence the door needs. A rig with no
 * mapped hand returns `[]` — "none found", which the caller must present as
 * itself and never as "no hand work needed" (§8: absence is a finding).
 */
export function handCandidates(rig: RigDescriptor, ontology: Ontology): readonly HandCandidate[] {
  const boneFor = new Map(rig.mapping.map((m) => [m.ontologyId, m.rigBone]));
  const childrenOf = new Map<string, string[]>();
  for (const bone of rig.bones) {
    if (bone.parent === null) continue;
    const kids = childrenOf.get(bone.parent) ?? [];
    kids.push(bone.name);
    childrenOf.set(bone.parent, kids);
  }

  const found: HandCandidate[] = [];
  for (const { hand, forearm, side } of HANDS) {
    // An ontology that has no such id is a wiring error, not a rig finding —
    // fail loud rather than silently reporting a handless rig (§8).
    if (!ontology.byId.has(hand)) {
      throw new Error(`ontology "${ontology.version}" has no "${hand}" — hand detection cannot be asked of it`);
    }
    const handBone = boneFor.get(hand);
    if (handBone === undefined) continue;
    found.push({
      handBone,
      forearmBone: boneFor.get(forearm) ?? null,
      side,
      ontologyId: hand,
      // Meshy merged-animation exports include a leaf Hand_End marker, not
      // a finger. Keep mapped bones and non-leaf attachments protected.
      existingChildren: (childrenOf.get(handBone) ?? []).filter(name =>
        name !== `${handBone}_End` || childrenOf.has(name) || rig.mapping.some(m => m.rigBone === name)),
    });
  }
  return found;
}
